import { useEffect, useRef, useState, type ReactNode } from 'react'

import type { PublicBannerResponse } from '@forbiz/contracts'

import s from './BannerCarousel.module.css'

const AUTO_MS = 5000

/** 운영자 이미지 배너 뒤에 붙는 코드 고정 슬라이드(예: 추천 배너). 같은 5:2 칸·도트·자동 슬라이드를 탄다. */
export interface ExtraSlide {
  key: string
  /** 접근성 이름(버튼 라벨). */
  label: string
  onClick: () => void
  content: ReactNode
}

/**
 * 홈 배너 캐러셀 (BIZ-111). 가로 스냅 스크롤(수동 스와이프) + 자동 슬라이드.
 * 자동 슬라이드는 2개 이상 · 사용자가 안 만지는 중 · reduced-motion이 아닐 때만.
 * 스크롤 위치가 활성 도트의 단일 원천이라 자동/수동이 서로 싸우지 않는다.
 */
export function BannerCarousel({
  items,
  onOpen,
  extraSlides = [],
}: {
  items: PublicBannerResponse[]
  onOpen: (link: string | null) => void
  extraSlides?: ExtraSlide[]
}) {
  const count = items.length + extraSlides.length
  const trackRef = useRef<HTMLDivElement>(null)
  const [active, setActive] = useState(0)
  const [paused, setPaused] = useState(false)

  useEffect(() => {
    if (count < 2 || paused) return
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
    const t = setInterval(() => {
      const el = trackRef.current
      if (!el) return
      const w = el.clientWidth
      const atEnd = el.scrollLeft + w >= el.scrollWidth - 4
      el.scrollTo({ left: atEnd ? 0 : el.scrollLeft + w, behavior: 'smooth' })
    }, AUTO_MS)
    return () => clearInterval(t)
  }, [count, paused])

  // 슬라이드 수가 바뀌면(배너 목록 갱신) 첫 슬라이드로 — 스냅이 이전에 보던 슬라이드를 붙잡아 중간부터 보이지 않게.
  useEffect(() => {
    const el = trackRef.current
    if (el) el.scrollLeft = 0
    setActive(0)
  }, [count])

  const onScroll = () => {
    const el = trackRef.current
    if (el) setActive(Math.round(el.scrollLeft / el.clientWidth))
  }
  const goTo = (i: number) => {
    const el = trackRef.current
    if (el) el.scrollTo({ left: i * el.clientWidth, behavior: 'smooth' })
  }

  return (
    <div
      className={s.wrap}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onPointerDown={() => setPaused(true)}
      onPointerUp={() => setPaused(false)}
      onPointerCancel={() => setPaused(false)}
    >
      <div className={s.track} ref={trackRef} onScroll={onScroll}>
        {items.map((b) =>
          b.linkUrl ? (
            <button key={b.id} type="button" className={s.banner} onClick={() => onOpen(b.linkUrl)}>
              <img className={s.img} src={b.imageUrl} alt={b.title} />
            </button>
          ) : (
            <span key={b.id} className={s.banner}>
              <img className={s.img} src={b.imageUrl} alt={b.title} />
            </span>
          ),
        )}
        {extraSlides.map((x) => (
          <button key={x.key} type="button" className={s.banner} aria-label={x.label} onClick={x.onClick}>
            {x.content}
          </button>
        ))}
      </div>
      {count > 1 ? (
        <div className={s.dots}>
          {[...items.map((b) => `b${b.id}`), ...extraSlides.map((x) => `x${x.key}`)].map((k, i) => (
            <button
              key={k}
              type="button"
              className={i === active ? `${s.dot} ${s.dotOn}` : s.dot}
              aria-label={`${i + 1}번 배너로 이동`}
              aria-current={i === active}
              onClick={() => goTo(i)}
            />
          ))}
        </div>
      ) : null}
    </div>
  )
}
